import { http } from '@/api/client/httpClient';
import type {
  HistoryFiltersInput,
  HistoryTableDto,
  LoginConnectorInput,
  LoginSearchItem,
  NopTableDto,
  OverViewInput,
  OverViewTableDto,
  OverviewSummaryDto,
} from '@/api/types/loginOverview';

const BASE = '/api/app/login-overview';

interface Paged<T> {
  items: T[];
  totalCount: number;
}

export const loginOverviewApi = {
  // POST (search returns paged value/text items; value = "LOGIN+CONNECTOR").
  // Paginated for select2 infinite scroll.
  async searchLogins(
    search: string,
    page = 1,
    pageSize = 15,
  ): Promise<{ items: LoginSearchItem[]; hasMore: boolean }> {
    const skipCount = (page - 1) * pageSize;
    const { data } = await http.post<Paged<LoginSearchItem>>(`${BASE}/search-logins`, {
      search,
      skipCount,
      maxResultCount: pageSize,
    });
    const items = data.items ?? [];
    return { items, hasMore: skipCount + items.length < (data.totalCount ?? 0) };
  },

  async getOverView(input: OverViewInput): Promise<OverViewTableDto> {
    const { data } = await http.get<OverViewTableDto>(`${BASE}/over-view`, { params: input });
    return data;
  },

  async getOverViewSummary(input: LoginConnectorInput): Promise<OverviewSummaryDto> {
    const { data } = await http.get<OverviewSummaryDto>(`${BASE}/over-view-summary`, { params: input });
    return data;
  },

  async getNop(input: LoginConnectorInput): Promise<NopTableDto> {
    const { data } = await http.get<NopTableDto>(`${BASE}/nop`, { params: input });
    return data;
  },

  async getHistory(input: HistoryFiltersInput): Promise<HistoryTableDto> {
    const { data } = await http.get<HistoryTableDto>(`${BASE}/history`, { params: input });
    return data;
  },
};

// Search value is "LOGIN+CONNECTOR" (e.g. "123456+1").
export function parseLoginValue(value: string): { login: number; connector: number } | null {
  const [l, c] = value.split('+');
  const login = Number(l);
  const connector = Number(c);
  if (!Number.isFinite(login) || !Number.isFinite(connector)) return null;
  return { login, connector };
}
